import React, { createContext, useContext, useMemo } from "react";
import { Connection } from "@solana/web3.js";
import { AnchorProvider, Program } from "@coral-xyz/anchor";
import { useAnchorWallet, useConnection } from "@solana/wallet-adapter-react";
import { HabitatStakingOffchain } from "../habitat_staking_offchain";
import { EnergyCredits } from "../energy_credits";
import stakingIdl from "../habitat_staking_offchain.json";
import ecIdl from "../energy_credits.json";
import { CONFIG } from "../config";

interface ProgramContextType {
  connection: Connection;
  program: Program<HabitatStakingOffchain> | null;
  ecProgram: Program<EnergyCredits> | null;
  provider: AnchorProvider | null;
}

const ProgramContext = createContext<ProgramContextType>({
  connection: new Connection(CONFIG.RPC_URL),
  program: null,
  ecProgram: null,
  provider: null,
});

export const useProgramContext = () => {
  const context = useContext(ProgramContext);
  if (!context) {
    throw new Error("useProgramContext must be used within ProgramProvider");
  }
  return context;
};

// Alias for backward compatibility
export const useProgram = useProgramContext;

interface ProgramProviderProps {
  children: React.ReactNode;
}

export const ProgramProvider: React.FC<ProgramProviderProps> = ({
  children,
}) => {
  const wallet = useAnchorWallet();
  const { connection } = useConnection();

  const provider = useMemo(() => {
    if (!wallet) return null;
    return new AnchorProvider(
      connection,
      wallet,
      AnchorProvider.defaultOptions()
    );
  }, [connection, wallet]);

  const program = useMemo(() => {
    if (!provider) return null;
    return new Program<HabitatStakingOffchain>(stakingIdl, provider);
  }, [provider]);

  const ecProgram = useMemo(() => {
    if (!provider) return null;
    return new Program<EnergyCredits>(ecIdl, provider);
  }, [provider]);

  return (
    <ProgramContext.Provider
      value={{ connection, program, ecProgram, provider }}
    >
      {children}
    </ProgramContext.Provider>
  );
};
